// src/contexts/ThemeContext.jsx — Dynamic brand color theming
import React, { createContext, useContext, useState, useEffect } from 'react';

const THEME_COLORS = [
  { id: 'purple', hex: '#7c3aed', label: 'بنفسجي' },
  { id: 'red',    hex: '#dc2626', label: 'أحمر' },
  { id: 'blue',   hex: '#2563eb', label: 'أزرق' },
  { id: 'green',  hex: '#059669', label: 'أخضر' },
  { id: 'gold',   hex: '#d97706', label: 'ذهبي' },
];

const STORAGE_KEY = 'sabeel-brand-color';
const DEFAULT_COLOR = '#7c3aed';

const ThemeContext = createContext();

// Compute a lighter shade for accents (roughly +25% lightness)
function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => { const k = (n + h / 30) % 12; return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1); };
  return '#' + [f(0), f(8), f(4)].map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('');
}

function lighten(hex, amount = 20) {
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(h, s, Math.min(100, l + amount));
}

function withAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function applyBrandColor(hex) {
  const root = document.documentElement.style;
  root.setProperty('--brand-main', hex);
  root.setProperty('--brand-main-light', lighten(hex, 25));
  root.setProperty('--brand-main-alpha-10', withAlpha(hex, 0.1));
  root.setProperty('--brand-main-alpha-20', withAlpha(hex, 0.2));
  root.setProperty('--brand-main-alpha-30', withAlpha(hex, 0.3));
  root.setProperty('--brand-main-alpha-50', withAlpha(hex, 0.5));
}

export const ThemeProvider = ({ children }) => {
  const [brandColor, setBrandColor] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_COLOR;
  });

  useEffect(() => {
    applyBrandColor(brandColor);
    localStorage.setItem(STORAGE_KEY, brandColor);
  }, [brandColor]);

  // Apply on first mount
  useEffect(() => { applyBrandColor(brandColor); }, []);

  return (
    <ThemeContext.Provider value={{ brandColor, setBrandColor, THEME_COLORS }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
